import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff, Lock, User } from "lucide-react";
import { useManagerAuth } from "@/hooks/useManagerAuth";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordSetup, setIsPasswordSetup] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const { session, isMemberAuthenticated, refreshMemberContext } = useManagerAuth();

  useEffect(() => {
    console.log('AuthPage: Component mounted, current URL:', window.location.href);
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('AuthPage: Auth state changed:', event, session?.user?.email);
        
        // Handle PASSWORD_RECOVERY explicitly - this means user clicked a reset/invite link
        if (event === 'PASSWORD_RECOVERY' && session?.user?.email) {
          console.log('AuthPage: PASSWORD_RECOVERY event detected → forcing password setup');
          setEmail(session.user.email);
          setIsPasswordSetup(true);
          setIsInitializing(false);
          return;
        }
        
        if (event === 'SIGNED_IN') {
          // Check if this is a password setup scenario
          checkAuthState();
        }
      }
    );

    // Initial check
    checkAuthState();

    return () => subscription.unsubscribe();
  }, []);

  // Redirect authenticated members to dashboard
  useEffect(() => {
    if (!isInitializing && isMemberAuthenticated && !isPasswordSetup) {
      console.log('AuthPage: Member is fully authenticated (password set), redirecting to dashboard');
      navigate('/manager/dashboard');
    }
  }, [isMemberAuthenticated, isInitializing, isPasswordSetup, navigate]);

  const checkAuthState = async () => {
    try {
      console.log('AuthPage: Checking auth state...');
      
      // Read URL flag once
      const urlInvited = searchParams.get('invited') === 'true';

      // Get current session first
      const { data: { session } } = await supabase.auth.getSession();

      // If we have a session and came from an invite link, force password setup UI
      if (session?.user?.email && urlInvited) {
        console.log('AuthPage: Invite flow detected with active session → forcing password setup');
        setEmail(session.user.email);
        setIsPasswordSetup(true);
        return;
      }
      
      // Check if user is logged in
      if (session?.user?.email) {
        console.log('AuthPage: User has active session, checking password status via auth_user_id');

        // Prefer checking by auth_user_id to satisfy RLS (user can always see their own row)
        const { data: memberByAuth, error: memberByAuthError } = await supabase
          .from('organization_members')
          .select('password_set')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (memberByAuthError) {
          console.error('AuthPage: Error checking member status (by auth_user_id):', memberByAuthError);
          setIsPasswordSetup(false);
        } else if (memberByAuth && memberByAuth.password_set === false) {
          console.log('AuthPage: Member exists but password not set → show setup');
          setEmail(session.user.email);
          setIsPasswordSetup(true);
        } else {
          console.log('AuthPage: Password already set or member not linked yet');
          setIsPasswordSetup(false);
        }
      } else {
        // No session - check URL parameters for invitation flow
        if (urlInvited) {
          console.log('AuthPage: Invitation URL detected but no session - user needs to check email');
          toast.error('Please check your email and click the invitation link to complete setup');
        }
        setIsPasswordSetup(false);
      }
      
    } catch (error) {
      console.error('AuthPage: Error checking auth state:', error);
      setIsPasswordSetup(false);
    } finally {
      setIsInitializing(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("Starting login process for:", email.toLowerCase());

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase(),
        password,
      });

      if (authError) {
        console.error("Authentication error:", authError);
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Login failed - no user returned');
      }

      console.log("Authentication successful for user:", authData.user.id);
      toast.success('Login successful!');

      // Refresh member context to ensure auth state is complete
      await refreshMemberContext();

    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      console.log("Password setup: Starting password setup for email:", email);

      // Update the user's password
      const { error: passwordError } = await supabase.auth.updateUser({
        password: password
      });

      if (passwordError) {
        console.error("Password setup: Error updating password:", passwordError);
        toast.error(passwordError.message || 'Failed to set password');
        return;
      }

      console.log("Password setup: Password updated successfully, updating member record...");
      
      // Get current session to get auth user ID
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession?.user) {
        console.error("Password setup: No session found after password update");
        toast.error('Session error after password update. Please try logging in.');
        return;
      }

      // Update member record to mark password as set and link auth user
      const { error: updateError } = await supabase
        .from('organization_members')
        .update({
          auth_user_id: currentSession.user.id,
          password_set: true,
          invitation_status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('email', email.toLowerCase());

      if (updateError) {
        console.error("Password setup: Error updating member record:", updateError);
        toast.error('Failed to complete account setup. Please contact support.');
        return;
      }

      console.log("Password setup: Member record updated successfully");
      toast.success('Password set successfully! Redirecting to dashboard...');

      // Refresh member context to load the updated member data
      await refreshMemberContext();
      
      // Navigate to dashboard
      setTimeout(() => {
        console.log("Password setup: Navigating to dashboard");
        navigate('/manager/dashboard');
      }, 1000);

    } catch (error: any) {
      console.error("Password setup: Fatal error:", error);
      toast.error(`Setup failed: ${error.message || 'Unknown error'}. Please contact support if this continues.`);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F4F6F8] to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-[#E6EEF5] shadow-lg">
          <CardContent className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F80ED] mx-auto mb-4"></div>
              <p className="text-[#2F80ED]">Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F6F8] to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#E6EEF5] shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center">
              {isPasswordSetup ? (
                <Lock className="w-8 h-8 text-[#2F80ED]" />
              ) : (
                <User className="w-8 h-8 text-[#2F80ED]" />
              )}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A237E]">
            {isPasswordSetup ? "Set Your Password" : "Welcome Back"}
          </CardTitle>
          <CardDescription className="text-[#2F80ED]">
            {isPasswordSetup 
              ? "Please set a password to complete your account setup"
              : "Enter your email and password to access your organization account"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={isPasswordSetup ? handlePasswordSetup : handleLogin} className="space-y-4">
            {!isPasswordSetup && (
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-[#E6EEF5] focus:border-[#2F80ED] focus:ring-[#2F80ED]"
                />
              </div>
            )}
            
            {isPasswordSetup && (
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  disabled
                  className="border-[#E6EEF5] bg-gray-50"
                />
              </div>
            )}

            <div className="space-y-2">
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={isPasswordSetup ? "New Password" : "Password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-[#E6EEF5] focus:border-[#2F80ED] focus:ring-[#2F80ED] pr-10"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2F80ED] hover:text-[#1A237E] transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {isPasswordSetup && (
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="border-[#E6EEF5] focus:border-[#2F80ED] focus:ring-[#2F80ED] pr-10"
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2F80ED] hover:text-[#1A237E] transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {password && password.length < 6 && (
                  <p className="text-sm text-red-500">Password must be at least 6 characters long</p>
                )}
                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-sm text-red-500">Passwords do not match</p>
                )}
              </div>
            )}

            {!isPasswordSetup && (
              <div className="text-sm text-right">
                <a 
                  href="/forgot-password"
                  className="text-[#2F80ED] hover:text-[#1A237E] hover:underline transition-colors"
                >
                  Forgot password?
                </a>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-[#1A237E] hover:bg-[#673AB7] text-white transition-colors"
              disabled={loading}
            >
              {loading 
                ? (isPasswordSetup ? "Setting Password..." : "Signing in...") 
                : (isPasswordSetup ? "Set Password" : "Sign In")
              }
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;